import { motion } from "framer-motion";
import React, { useEffect } from "react";
import { CallIcon, VideoIcon2 } from "../icons/icons";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/AboutHeroSection-fa.json";
import en from "./locales/AboutHeroSection-en.json";
import ar from "./locales/AboutHeroSection-ar.json";
import tr from "./locales/AboutHeroSection-tr.json";

interface HeroSectionProps {
    onContactClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onContactClick }) => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "AboutHeroSection";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    // Determine text direction based on current language
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    const handleContactClick = () => {
        const contactElement = document.getElementById("contact-section") || document.querySelector(".contact-us") || document.querySelector('[data-section="contact"]');

        if (contactElement) {
            contactElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        } else {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
            });
        }

        if (onContactClick) {
            onContactClick();
        }
    };

    const handleVideoClick = () => {
        window.open(t("videoUrl", { ns: "AboutHeroSection" }), "_blank");
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="relative pt-0 sm:pt-12 overflow-x-hidden">
            {/* Ambient Background Aura */}
            <div className="absolute top-[-8%] left-1/2 transform -translate-x-1/2 w-[650px] h-[360px] bg-gradient-to-b from-blue-400/20 via-sky-300/15 to-transparent rounded-full blur-[130px] z-0 pointer-events-none"></div>
            <div className="absolute top-[20%] right-[-10%] w-[350px] h-[350px] bg-indigo-400/10 rounded-full blur-[100px] z-0 pointer-events-none"></div>

            <div className="container mx-auto px-4 pt-3 sm:pt-12 flex flex-col justify-between">
                <div className="flex flex-col items-center text-center relative z-10 -mb-16 md:pt-6">
                    {/* Delband Ecosystem Pill */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50/80 border border-blue-200/60 text-[#0072CE] text-xs sm:text-sm font-semibold mb-6 shadow-sm backdrop-blur-md"
                        style={{ direction: dir }}
                    >
                        <span className="w-2 h-2 rounded-full bg-[#0072CE] animate-pulse"></span>
                        <span>Med-3.0 &amp; Digital Health Innovation</span>
                    </motion.div>

                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-6 text-slate-900 tracking-tight leading-tight" style={{ direction: dir }}>
                        {t("heading.regular", { ns: "AboutHeroSection" })}{" "}
                        <span className="bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] bg-clip-text text-transparent">
                            {t("heading.highlight", { ns: "AboutHeroSection" })}
                        </span>
                    </h1>

                    {/* متن ترکیبی برای موبایل */}
                    <p className={`text-base sm:text-lg text-slate-600 max-w-3xl mb-6 block sm:hidden leading-relaxed ${textAlign}`} style={{ direction: dir }}>
                        {t("description.mobile", { ns: "AboutHeroSection" })}
                    </p>

                    {/* متن جدا برای دسکتاپ */}
                    <div className="hidden sm:block">
                        <p className="text-lg text-slate-600 max-w-3xl mb-4 leading-relaxed font-normal" style={{ direction: dir }}>
                            {t("description.desktop", { ns: "AboutHeroSection" })}
                        </p>
                    </div>

                    {/* دکمه‌ها */}
                    <div className={`flex flex-col sm:flex-row w-full gap-3 sm:gap-4 mt-4 justify-center items-center ${isRTL ? "sm:flex-row-reverse" : "sm:flex-row"}`}>
                        <motion.button
                            whileHover={{
                                scale: 1.03,
                                boxShadow: "0 14px 28px -6px rgba(0, 114, 206, 0.35)",
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleVideoClick}
                            className="text-sm sm:text-base bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white flex items-center justify-center px-6 py-3.5 rounded-2xl whitespace-nowrap cursor-pointer font-semibold shadow-lg shadow-blue-500/20 transition-all duration-300 w-full sm:w-auto"
                            type="button"
                        >
                            <span>{t("buttons.video", { ns: "AboutHeroSection" })}</span>
                            <VideoIcon2 primaryColor="white" className={`${isRTL ? "mr-2" : "ml-2"} mb-0.5`} />
                        </motion.button>
                        <motion.button
                            onClick={handleContactClick}
                            whileHover={{
                                scale: 1.03,
                                boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.1)",
                            }}
                            whileTap={{ scale: 0.98 }}
                            className="text-sm sm:text-base bg-white/90 backdrop-blur-md text-slate-700 border border-slate-200/90 hover:border-blue-300 hover:text-[#0072CE] flex items-center justify-center px-6 py-3.5 rounded-2xl whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 w-full sm:w-auto shadow-sm"
                            type="button"
                        >
                            <span>{t("buttons.contact", { ns: "AboutHeroSection" })}</span>
                            <CallIcon className={`${isRTL ? "mr-2" : "ml-2"} mb-0.5`} />
                        </motion.button>
                    </div>
                </div>

                {/* تصویر */}
                <div className="relative z-0 mt-6">
                    <div className="w-full h-full relative overflow-hidden pt-12 sm:pt-4">
                        {/* موبایل */}
                        <img
                            src={t("images.mobile.src", { ns: "AboutHeroSection" })}
                            alt={t("images.mobile.alt", { ns: "AboutHeroSection" })}
                            className="w-full h-full object-cover object-top sm:hidden drop-shadow-md rounded-2xl"
                        />

                        {/* دسکتاپ */}
                        <img
                            src={t("images.desktop.src", { ns: "AboutHeroSection" })}
                            alt={t("images.desktop.alt", { ns: "AboutHeroSection" })}
                            className="w-full h-full object-cover object-top hidden sm:block drop-shadow-xl rounded-2xl"
                        />
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default HeroSection;
